// UTC hourly two-sample staircase encoding for calibration series.
// Port of compute/py_modules/continuous_calibration/operation/steps/hourly_series_encoding.py

const SECONDS_PER_HOUR = 3600;

/**
 * @param {{ event_time: number, rolling_inventory: number }[]} sortedEvents
 * @param {number} t
 * @returns {number | null}
 */
export function valueAtOrBefore(sortedEvents, t) {
  let value = null;
  for (const event of sortedEvents) {
    if (event.event_time <= t) {
      value = event.rolling_inventory;
    } else {
      break;
    }
  }
  return value;
}

/**
 * UTC hour boundaries te such that (te-3600, te] overlaps [tMin, tMax].
 * @param {number} tMin
 * @param {number} tMax
 * @returns {number[]}
 */
export function hourEndRange(tMin, tMax) {
  const firstTe = Math.floor(tMin / SECONDS_PER_HOUR + 1) * SECONDS_PER_HOUR;
  const lastTe = Math.floor((tMax + SECONDS_PER_HOUR - 1) / SECONDS_PER_HOUR) * SECONDS_PER_HOUR;
  if (firstTe > lastTe) {
    return [];
  }
  const hourEnds = [];
  for (let te = firstTe; te <= lastTe; te += SECONDS_PER_HOUR) {
    hourEnds.push(te);
  }
  return hourEnds;
}

/**
 * @param {{ event_time: number, rolling_inventory: number }[]} events
 * @param {{ tEnd?: number | null }} [options]
 * @returns {{ t: number, v: number }[]}
 */
export function dataframeToHourlyStepPoints(events, { tEnd = null } = {}) {
  if (!events?.length) {
    return [];
  }

  const sortedEvents = [...events].sort((a, b) => a.event_time - b.event_time);
  const tMin = sortedEvents[0].event_time;
  const tMax = sortedEvents[sortedEvents.length - 1].event_time;
  const tMaxEff = tEnd != null ? Math.max(tMax, tEnd) : tMax;
  const hourEnds = hourEndRange(tMin, tMaxEff);

  if (!hourEnds.length) {
    const te = Math.max(
      Math.floor((tMaxEff + SECONDS_PER_HOUR - 1) / SECONDS_PER_HOUR) * SECONDS_PER_HOUR,
      SECONDS_PER_HOUR,
    );
    const vNew = valueAtOrBefore(sortedEvents, tMax);
    const vOld = valueAtOrBefore(sortedEvents, te - SECONDS_PER_HOUR);
    if (vNew == null) {
      return [];
    }
    return [
      { t: te, v: vOld ?? vNew },
      { t: te, v: vNew },
    ];
  }

  const points = [];
  let prevEndValue = null;

  for (const te of hourEnds) {
    let vOld = valueAtOrBefore(sortedEvents, te - SECONDS_PER_HOUR);
    const vNew = valueAtOrBefore(sortedEvents, te);
    if (vOld == null) {
      vOld = prevEndValue ?? vNew;
    }
    if (vNew == null) {
      continue;
    }
    if (vOld == null) {
      vOld = vNew;
    }
    points.push({ t: te, v: vOld });
    points.push({ t: te, v: vNew });
    prevEndValue = vNew;
  }

  return points;
}

/**
 * @param {{ t: number, v: number }[]} points
 * @param {number} nowTs
 * @param {number | null} valueAtNow
 * @param {{ trimFuture: boolean }} options
 * @returns {{ t: number, v: number }[]}
 */
export function finalizeTvPointsWithNow(points, nowTs, valueAtNow, { trimFuture }) {
  if (valueAtNow == null) {
    if (trimFuture) {
      return points.filter((p) => p.t <= nowTs);
    }
    return points;
  }

  if (trimFuture) {
    const out = points.filter((p) => p.t <= nowTs);
    if (!out.length || out[out.length - 1].t < nowTs) {
      out.push({ t: nowTs, v: valueAtNow });
    }
    return out;
  }

  const before = points.filter((p) => p.t <= nowTs);
  const after = points.filter((p) => p.t > nowTs);
  const needBridge = !before.length || before[before.length - 1].t < nowTs;
  const bridge = needBridge ? [{ t: nowTs, v: valueAtNow }] : [];
  return [...before, ...bridge, ...after];
}

/**
 * @param {{ event_time: number, rolling_inventory: number }[]} events
 * @param {{ nowTs?: number | null, trimFuture?: boolean }} [options]
 * @returns {{ t: number, v: number }[]}
 */
export function aggregateSeriesToTvPoints(events, { nowTs = null, trimFuture = true } = {}) {
  if (!events?.length) {
    return [];
  }

  const sortedEvents = [...events].sort((a, b) => a.event_time - b.event_time);
  const tEnd = nowTs != null && trimFuture ? nowTs : null;
  let points = dataframeToHourlyStepPoints(sortedEvents, { tEnd });

  if (nowTs != null) {
    const vNow = valueAtOrBefore(sortedEvents, nowTs);
    points = finalizeTvPointsWithNow(points, nowTs, vNow, { trimFuture });
  }

  return points;
}
